import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import styles from "./styles.module.css";
import ImageComponent from "../ImageComponent";
import ProgressBar from "../ProgressBar";
import GameStatusNotch from "../GameStatusNotch";
import { debounce } from "lodash";

import icon1 from "../../assets/badge-icon.svg";
import icon2 from "../../assets/trophy-icon.svg";
import icon3 from "../../assets/thumbsup-icon.svg";
import { useTranslation } from "react-i18next";
import { SKELETON_TIMEOUT } from "../../appConstants/AppConstant";
import { useNavigate } from "react-router-dom";
import { CLEVERTAP_EVENTS, NAVIGATION_ROUTES } from "../../constant";
import {
  GeneralInfo,
  SortOption,
  Widget,
  WidgetItem,
  WidgetStyle,
} from "../../interfaces/TournamentsConfig";
import { useTokenStore } from "../../storage/tokenState";
import _isEmpty from "lodash/isEmpty";
import { SCREEN_NAMES } from "../../utils/helpers/constants";
import { pushClevertapEvent } from "../../utils/analytics/clevertap";
import { useTournamentNavigation } from "../../utils/helpers/tournamentNavigationHelper";
import { handleClickCTEventRegisterTournamentNonLoggedInUser } from "../../utils/events/events";
import { getPartnerId } from "../../utils/helpers/common";
import { FbPixelEvent_Lead } from "../../utils/FbPixel/fbPixel";
import { FACEBOOK } from "../../utils/helpers/constants";

const LUDONX = "ludonx"
interface TournamentCardProps {
  firstPrize: string;
  prizePool: string;
  assuredWinners: string;
  entries: number;
  maxEntries: number;
  timeRemaining: string;
  isDisabled?: boolean;
  isMegaTournament?: boolean;
}

const TournamentCard: React.FC<Widget> = ({ items, style, isCollapsable = false, widgetIndex, firstMegaTournamentIndex }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token: userToken } = useTokenStore();
  const [loading, setLoading] = useState(true);
  const [isClicking, setIsClicking] = useState<boolean>(false);
  const tournament = items[0]
  const gameName = LUDONX
  const {
    name,
    generalInfo,
    isMegaTournament,
    registrationEndTime,
    maxParticipants = 0,
    numRegistrations = 0,
    ltid,
    skipTournamentDetailsPage = false
  } = tournament;
  const gameFormatType = tournament?.gameFormatType || null;
  const gameFormatImage = tournament?.gameFormatImage || null;
  const { prizePoolText, entryFeeText, firstPrizeText, assuredWinnersText } =
    generalInfo || ({} as GeneralInfo);

  const {
    isBtnDisabled,
    registerLoading,
    isPolling,
    handleNavigation,
    LoaderOverlay
  } = useTournamentNavigation({ gameName, ltid });

  useEffect(() => {
    setTimeout(() => setLoading(false), SKELETON_TIMEOUT);
  }, []);

  const [visibleIcons, setVisibleIcons] = useState<Record<string, boolean>>({
    icon1: false,
    icon2: false,
    icon3: false,
  });

  const iconRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    iconRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            const iconKey = `icon${index + 1}`;
            setVisibleIcons((prev) => ({ ...prev, [iconKey]: true }));
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(ref);

      return () => {
        observer.disconnect();
      };
    });
  }, []);

  const iconData = [
    {
      src: icon1,
      alt: "First Prize Icon",
      label: t("firstPrize"),
      value: firstPrizeText || "",
      class: "icon1",
      fontSize: "fontSizeSm20",
    },
    {
      src: icon2,
      alt: "Prize Pool Icon",
      label: t("prizePool"),
      value: prizePoolText || "",
      class: "icon2",
      fontSize: "fontSizeSm12",
    },
    {
      src: icon3,
      alt: "Assured Winners Icon",
      label: t("assuredWinners"),
      value: assuredWinnersText || "",
      class: "icon3",
      fontSize: "fontSizeSm12",
    },
  ];

  // Clevertap events
  const handleClickCTEventForNonLoggedInUser = useCallback(() => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_PHONE_NUMBER_SCREEN, {
      "location": "lobby",
      "cardType": isMegaTournament ? "megaTournamentCard" : "tournamentCard"
    });
  }, [isMegaTournament]);

  const handleClickCTEventForLoggedInUser = useCallback(() => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_REGISTER_TOURNAMENT, {
      "location": "lobby",
      "cardType": isMegaTournament ? "megaTournamentCard" : "tournamentCard"
    });
  }, [isMegaTournament]);

  const debouncedPlayTournament = useCallback(
    debounce((e?: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      if (e) {
        e.stopPropagation();
      }
      if (registerLoading || isPolling || isClicking) return;
      if(getPartnerId() === FACEBOOK){
        FbPixelEvent_Lead();
      }
      setIsClicking(true);
      if (!_isEmpty(userToken)) {
        handleClickCTEventForLoggedInUser();
        handleNavigation();
      } else {
        handleClickCTEventForNonLoggedInUser();
        handleClickCTEventRegisterTournamentNonLoggedInUser();
        localStorage.setItem('gameDetails', JSON.stringify({ gameName, ltid }));
        localStorage.setItem('runGameplay', 'true');
        navigate(NAVIGATION_ROUTES.SIGNIN, { state: { screenName: SCREEN_NAMES.TOURNAMENT_LOBBY } });
      }
      // Reset clicking state after a delay to prevent rapid clicks
      setTimeout(() => setIsClicking(false), 1000);
    }, 300),
    [registerLoading, isPolling, userToken, handleNavigation, navigate, isClicking]
  );

  return (
    <>
      <LoaderOverlay />
      <div
        className={clsx(styles.cardBox, {
          [styles.disabled]: isBtnDisabled || registerLoading || isPolling || isClicking
        })}
        onClick={debouncedPlayTournament}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            debouncedPlayTournament();
          }
        }}
      >
        {gameFormatType === 'NX_21' && gameFormatImage && (
          <img
            src={gameFormatImage}
            alt="image"
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 10,
            }}
          />
        )}
        <div
          className={clsx(styles.firstHalfBlock, {
            [styles.isMegaTournament]: isMegaTournament,
          })}
        >
          {isMegaTournament ? (
            <div
              className={clsx(
                styles.badgeText,
                "fontSizeXs fontWeightBold fontColorSecondary textUppercase"
              )}
            >
              {t("mega")}
            </div>
          ) : (
            ""
          )}
          {iconData.map(
            ({ src, alt, label, value, class: iconClass, fontSize }, index) => (
              <div
                className={clsx(styles.item)}
                key={index}
                ref={(el) => (iconRefs.current[index] = el)}
              >
                <ImageComponent
                  src={src}
                  alt={alt}
                  className={clsx(styles.icon, styles[iconClass], {
                    [styles.animate]: visibleIcons[`icon${index + 1}`],
                  })}
                />
                <div>
                  <label
                    className={clsx(
                      styles.label,
                      "fontWeightRegular fontSizeXs fontColor666 textUppercase"
                    )}
                  >
                    {label}
                  </label>
                  <div
                    className={clsx(
                      styles.content,
                      fontSize,
                      "fontWeightBold fontColorBlack"
                    )}
                  >
                    {value}
                  </div>
                </div>
              </div>
            )
          )}
        </div>

        <div className={clsx(styles.secondHalfBlock)}>
          {isMegaTournament &&
            <GameStatusNotch
              tournament={tournament}
            />
          }
          <div className={clsx(styles.tournamentEntries)}>
            <label
              className={clsx(
                styles.label,
                "fontWeightRegular fontSizeXs fontColor666 textUppercase"
              )}
            >
              {t("tournamentEntries")}
            </label>
            <ProgressBar value={numRegistrations} max={maxParticipants} />
          </div>
          <div className={clsx(styles.playBtnBox)}>
            <button
              className={clsx(styles.playBtn, "btn primaryBtn mdHeight")}
              disabled={isCollapsable || isBtnDisabled || registerLoading || isPolling || isClicking}
            // onClick={debouncedPlayTournament}
            >
              {t("playForFree")}
            </button>
            {!isCollapsable && isMegaTournament && firstMegaTournamentIndex === widgetIndex && (
              <ImageComponent
                src={"https://static-perf1.zupee.com/blog-images/uploads/2025/04/hand-gesture-purple.png"}
                alt="hand gesture"
                className={clsx(styles.handGestureIcon)}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

interface TournamentCardsProps {
  tournaments: Widget[];
}

const TournamentCards: React.FC<TournamentCardsProps> = ({ tournaments }) => {
  {
    console.log("tournamentstournaments", tournaments);
  }

  return (
    <div className={clsx(styles.tournamentCardsContainer)}>
      {tournaments &&
        tournaments?.map(
          (tournament, index) =>
            index > 0 && <TournamentCard key={index} {...tournament} />
        )}
    </div>
  );
};

export default TournamentCard;
